import { useEffect, useRef } from "react";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { database, firestore } from "services/firebaseService/firebaseConfig";
import { useI18n } from "i18n";
import { selectNetworkIsConnected } from "modules/network/redux/selectors";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import {
  resetNetwork,
  setConnected,
  setDisconnected,
} from "modules/network/redux/actions";
import { goOffline, goOnline } from "firebase/database";
import { disableNetwork, enableNetwork } from "firebase/firestore";

const NetworkTracker = (): null => {
  const { t } = useI18n();
  const messageId = "network-status";
  const isConnected = useSelector(selectNetworkIsConnected);
  const isOfflineBefore = useRef(false);
  const dispatch = useAppDispatch();
  const { addSuccessNotification, addErrorNotification, removeNotification } =
    useNotificationActions();

  const handleConnect = () => {
    removeNotification(messageId);
    // force firestore connect
    goOnline(database);
    enableNetwork(firestore);
    // wait for 200ms
    setTimeout(() => {
      dispatch(setConnected());
    }, 200);
  };
  const handleDisconnect = () => {
    goOffline(database);
    disableNetwork(firestore);

    addErrorNotification({
      uid: messageId,
      message: t("network.disconnected"),
      autoDismiss: 120,
    });
    isOfflineBefore.current = true;
    dispatch(setDisconnected());
  };

  useEffect(() => {
    if (isOfflineBefore.current && isConnected) {
      isOfflineBefore.current = false;
      addSuccessNotification({
        message: t("network.reconnected"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    window.addEventListener("online", handleConnect);
    window.addEventListener("offline", handleDisconnect);

    return () => {
      window.removeEventListener("online", handleConnect);
      window.removeEventListener("offline", handleDisconnect);
      dispatch(resetNetwork());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default NetworkTracker;
